/*
  Blog filters — Storefront API
  ------------------------------------------------------------------
  Filtro superior (tags, ej. "topic:iPhone"): se resuelve en el servidor
  vía el argumento `query` de Blog.articles ("tag:'...'"), así que escala
  y pagina de verdad.

  Filtro inferior (metacampo, ej. custom.content_category = "Comparativas"):
  la Storefront API no soporta filtrar articles por metacampo en el
  servidor (solo tag/tag_not/author/blog_title/created_at/updated_at), así
  que se trae el valor en la misma respuesta y se filtra en el cliente.
  Por eso, si hay un filtro de categoría activo, se sigue pidiendo páginas
  adicionales automáticamente hasta juntar suficientes resultados visibles
  (o agotar el blog) — "smart load more".
*/
(function () {
  "use strict";

  var ROOT_SELECTOR = "[data-blog-filter-root]";
  var MAX_AUTO_PAGES = 6; // límite de páginas que se auto-piden en una sola pasada cuando el filtro de categoría deja pocos/ningún resultado visible

  function init(root) {
    var config = {
      shop: root.dataset.shop,
      token: root.dataset.token,
      apiVersion: root.dataset.apiVersion || "2025-01",
      blogHandle: root.dataset.blogHandle,
      metaNamespace: root.dataset.metaNamespace || "custom",
      metaKey: root.dataset.metaKey || "content_category",
      metaobjectField: root.dataset.metaobjectField || "category",
      pageSize: parseInt(root.dataset.pageSize, 10) || 12,
    };

    console.log("[blog-filters] init() en root:", root, "config:", config);

    if (!config.shop || !config.token || !config.blogHandle) {
      console.warn("blog-filters: faltan data-shop/data-token/data-blog-handle en", root);
      return;
    }

    var listEl = root.querySelector("[data-blog-filter-list]");
    var loadMoreBtn = root.querySelector("[data-blog-filter-load-more]");
    var emptyStateEl = root.querySelector("[data-blog-filter-empty]");
    var clearTagBtn = root.querySelector("[data-tag-filter-clear]");
    var tagButtons = Array.prototype.slice.call(root.querySelectorAll("[data-tag-filter]"));
    var categoryButtons = Array.prototype.slice.call(root.querySelectorAll("[data-category-filter]"));

    console.log("[blog-filters] listEl:", listEl, "tagButtons encontrados:", tagButtons.length, "categoryButtons encontrados:", categoryButtons.length);

    if (!listEl) {
      console.warn("blog-filters: falta [data-blog-filter-list] dentro de", root);
      return;
    }

    var state = {
      activeTags: [],
      activeCategory: null,
      endCursor: null,
      hasNextPage: true,
      loading: false,
    };

    // categoryValue ya no es un metacampo de texto/lista directo: es una
    // referencia ("one") a un metaobject. Hay que resolver reference { ... on
    // Metaobject { field(key: $metaobjectField) { value } } } para llegar al
    // texto real (que a su vez es list.single_line_text_field dentro del
    // metaobject, por eso field.value sigue siendo un array JSON serializado).
    var QUERY =
      "query BlogArticles($handle: String!, $first: Int!, $after: String, $filterQuery: String, $namespace: String!, $key: String!, $metaobjectField: String!) {" +
      "  blog(handle: $handle) {" +
      "    articles(first: $first, after: $after, query: $filterQuery, sortKey: PUBLISHED_AT, reverse: true) {" +
      "      edges {" +
      "        cursor" +
      "        node {" +
      "          id" +
      "          title" +
      "          handle" +
      "          onlineStoreUrl" +
      "          excerpt" +
      "          image { url altText width height }" +
      "          publishedAt" +
      "          tags" +
      "          categoryValue: metafield(namespace: $namespace, key: $key) {" +
      "            reference {" +
      "              ... on Metaobject {" +
      "                field(key: $metaobjectField) { value }" +
      "              }" +
      "            }" +
      "          }" +
      "        }" +
      "      }" +
      "      pageInfo { hasNextPage endCursor }" +
      "    }" +
      "  }" +
      "}";

    function escapeHtml(str) {
      return String(str || "").replace(/[&<>"']/g, function (ch) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch];
      });
    }

    function truncateWords(str, count) {
      var words = String(str || "").trim().split(/\s+/);
      if (words.length <= count) return words.join(" ");
      return words.slice(0, count).join(" ") + "…";
    }

    function formatDate(iso) {
      if (!iso) return "";
      var d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "2-digit" });
    }

    // El metacampo es "lista de single line text" (list.single_line_text_field):
    // la Storefront API devuelve su value como un string con un array JSON,
    // ej. '["Guías de compra"]' o '["Guías de compra","Novedades"]' si el
    // artículo tiene más de una categoría — nunca un string plano.
    function parseCategoryList(rawValue) {
      if (!rawValue) return [];
      try {
        var parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed : [String(parsed)];
      } catch (e) {
        return [rawValue];
      }
    }

    // categoryValue (metafield) -> reference (Metaobject) -> field (list de
    // texto). Cualquier eslabón puede venir null (artículo sin categoría
    // asignada), así que se navega con encadenamiento defensivo.
    function categoryRawValue(article) {
      return article.categoryValue &&
        article.categoryValue.reference &&
        article.categoryValue.reference.field
        ? article.categoryValue.reference.field.value
        : null;
    }

    function buildTagQuery() {
      if (!state.activeTags.length) return null;
      return state.activeTags
        .map(function (tag) {
          return "tag:'" + String(tag).replace(/'/g, "\\'") + "'";
        })
        .join(" AND ");
    }

    function runQuery() {
      var endpoint = "https://" + config.shop + "/api/" + config.apiVersion + "/graphql.json";
      console.log("[blog-filters] runQuery() ->", endpoint, "variables:", {
        handle: config.blogHandle,
        first: config.pageSize,
        after: state.endCursor,
        filterQuery: buildTagQuery(),
        namespace: config.metaNamespace,
        key: config.metaKey,
        metaobjectField: config.metaobjectField,
      });
      return fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": config.token,
        },
        body: JSON.stringify({
          query: QUERY,
          variables: {
            handle: config.blogHandle,
            first: config.pageSize,
            after: state.endCursor,
            filterQuery: buildTagQuery(),
            namespace: config.metaNamespace,
            key: config.metaKey,
            metaobjectField: config.metaobjectField,
          },
        }),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (json) {
          console.log("[blog-filters] respuesta cruda:", json);
          if (json.errors) {
            console.error("blog-filters: error de GraphQL", json.errors);
            return null;
          }
          return json.data && json.data.blog ? json.data.blog.articles : null;
        })
        .catch(function (err) {
          console.error("blog-filters: fetch fallido", err);
          return null;
        });
    }

    var ARROW_ICON_SVG =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
      '<path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>";

    // Badge de la card = tipo de producto (mismo tag "filter_X" de los chips
    // de arriba), NO la categoría del metacampo — Figma muestra "Mac"/"iPhone"/
    // "Watch" en cada card, no "Guías de compra".
    var PRODUCT_BADGE_LABELS = {
      filter_iPhone: "iPhone",
      filter_iPad: "iPad",
      filter_Mac: "Mac",
      "filter_Apple Watch": "Apple Watch",
      filter_AirPods: "AirPods",
      filter_Accesorios: "Accesorios",
      filter_AppleTV: "Apple TV",
    };

    function productBadgeFor(tags) {
      var list = tags || [];
      for (var i = 0; i < list.length; i++) {
        if (PRODUCT_BADGE_LABELS[list[i]]) return PRODUCT_BADGE_LABELS[list[i]];
      }
      return "";
    }

    function renderCard(article) {
      var categoryLabel = parseCategoryList(categoryRawValue(article)).join(" ");
      var productBadge = productBadgeFor(article.tags);
      var img = article.image
        ? '<img src="' + escapeHtml(article.image.url) + '" alt="' + escapeHtml(article.image.altText || "") + '" loading="lazy" width="600">'
        : "";
      var excerpt = article.excerpt ? truncateWords(article.excerpt, 15) : "";

      return (
        '<li class="article_item" data-categories="' + escapeHtml(categoryLabel) + '">' +
        '<a href="' + escapeHtml(article.onlineStoreUrl) + '">' +
        '<div class="title_media">' + img + "</div>" +
        '<div class="title_description">' +
        (productBadge ? '<span class="mb-badge mb-badge--sm">' + escapeHtml(productBadge) + "</span>" : "") +
        "<h2>" + escapeHtml(article.title) + "</h2>" +
        (excerpt ? '<p class="title_description--excerpt">' + escapeHtml(excerpt) + "</p>" : "") +
        '<div class="title_description--footer">' +
        '<p class="title_description--date">' + formatDate(article.publishedAt) + "</p>" +
        '<span class="title_description--link">Ver más' + ARROW_ICON_SVG + "</span>" +
        "</div>" +
        "</div>" +
        "</a>" +
        "</li>"
      );
    }

    function toggleLoading(isLoading) {
      root.classList.toggle("is-loading", isLoading);
      // loadMoreBtn es un <a>, no un <button> — no tiene .disabled real;
      // aria-disabled + el guard de state.loading en fetchAndRender alcanzan.
      if (loadMoreBtn) loadMoreBtn.setAttribute("aria-disabled", isLoading ? "true" : "false");
    }

    function updateEmptyState(visibleCount) {
      if (!emptyStateEl) return;
      emptyStateEl.hidden = visibleCount > 0;
    }

    function updateLoadMoreVisibility() {
      if (!loadMoreBtn) return;
      loadMoreBtn.hidden = !state.hasNextPage;
    }

    function fetchAndRender(reset) {
      console.log("[blog-filters] fetchAndRender(reset=" + reset + ") — activeTags:", state.activeTags, "activeCategory:", state.activeCategory, "loading:", state.loading);
      if (state.loading) return;
      state.loading = true;

      if (reset) {
        state.endCursor = null;
        state.hasNextPage = true;
        listEl.innerHTML = "";
      }

      toggleLoading(true);

      var visibleBefore = listEl.children.length;
      var visibleThisRun = 0;
      var pagesFetchedThisRun = 0;

      function fetchNextPage() {
        return runQuery().then(function (connection) {
          pagesFetchedThisRun++;

          if (!connection) {
            state.hasNextPage = false;
            return;
          }

          state.endCursor = connection.pageInfo.endCursor;
          state.hasNextPage = connection.pageInfo.hasNextPage;

          connection.edges.forEach(function (edge) {
            var node = edge.node;
            var categoryValues = parseCategoryList(categoryRawValue(node));
            var matchesCategory = !state.activeCategory || categoryValues.indexOf(state.activeCategory) !== -1;
            if (matchesCategory) {
              visibleThisRun++;
              listEl.insertAdjacentHTML("beforeend", renderCard(node));
            }
          });

          // Sin filtro de categoría, una página basta — el filtro de tag ya
          // vino resuelto del servidor. Con filtro de categoría activo,
          // seguimos pidiendo páginas mientras no aparezcan resultados
          // visibles y queden páginas, hasta el límite de seguridad.
          var needsMore = state.activeCategory && visibleThisRun === 0 && state.hasNextPage && pagesFetchedThisRun < MAX_AUTO_PAGES;

          if (needsMore) {
            return fetchNextPage();
          }
        });
      }

      fetchNextPage().then(function () {
        console.log("[blog-filters] fetchAndRender terminó — visibleThisRun:", visibleThisRun, "hasNextPage:", state.hasNextPage);
        updateEmptyState(visibleBefore + visibleThisRun);
        updateLoadMoreVisibility();
        toggleLoading(false);
        state.loading = false;
      });
    }

    function setActiveButton(buttons, activeButton) {
      buttons.forEach(function (btn) {
        btn.classList.toggle("is-active", btn === activeButton);
        btn.setAttribute("aria-pressed", btn === activeButton ? "true" : "false");
      });
    }

    // No hay chip "Todos": el filtro se limpia con el link data-tag-filter-clear.
    // Los chips activos tienen pointer-events:none (CSS), así que en la práctica
    // solo llega el click de un chip distinto al activo — single-select real.
    tagButtons.forEach(function (button) {
      button.addEventListener("click", function () {
        var tag = button.dataset.tagFilter;
        console.log("[blog-filters] click en tag-filter:", tag);
        if (!tag) return;

        state.activeTags = [tag];
        setActiveButton(tagButtons, button);
        if (clearTagBtn) clearTagBtn.hidden = false;

        fetchAndRender(true);
      });
    });

    if (clearTagBtn) {
      clearTagBtn.addEventListener("click", function (event) {
        event.preventDefault();
        console.log("[blog-filters] click en limpiar filtro");
        state.activeTags = [];
        setActiveButton(tagButtons, null);
        clearTagBtn.hidden = true;
        fetchAndRender(true);
      });
    }

    categoryButtons.forEach(function (button) {
      button.addEventListener("click", function () {
        var category = button.dataset.categoryFilter;
        var isSame = state.activeCategory === category;
        console.log("[blog-filters] click en category-filter:", category, "activeCategory(antes):", state.activeCategory);

        state.activeCategory = category === "all" || isSame ? null : category;
        setActiveButton(categoryButtons, state.activeCategory ? button : categoryButtons.filter(function (b) {
          return b.dataset.categoryFilter === "all";
        })[0]);

        fetchAndRender(true);
      });
    });

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", function (event) {
        event.preventDefault();
        if (state.loading) return;
        fetchAndRender(false);
      });
    }

    fetchAndRender(true);
  }

  function bootstrap() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }
})();
